import { CheckIcon, ChevronsUpDownIcon, LayersIcon, PlusIcon } from 'lucide-react'
import { Link } from 'react-router'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useActiveZone } from '@/zone/useActiveZone'

/**
 * The console's most important control: everything below the shell is scoped to
 * whatever this says. It shows the zone name in mono (names are identifiers,
 * §12) and marks a `DEACTIVATED` or `ARCHIVED` zone inline, because "why can
 * nothing connect" is otherwise a silent mystery. Archived zones are listed:
 * their history is readable forever (§17) and this is the way to it.
 */
export function ZoneSwitcher() {
  const { zones, activeZone, selectZone, isLoading, isError } = useActiveZone()

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />
  }

  if (isError || zones.length === 0) {
    return (
      <Button asChild variant="outline" className="w-full justify-start">
        <Link to="/app/zones">
          <LayersIcon className="size-4" />
          {isError ? 'Zones unavailable' : 'No zones yet'}
        </Link>
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-auto w-full justify-between px-3 py-2 text-left"
          aria-label="Switch zone"
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <span
              aria-hidden="true"
              className={cn(
                'size-1.5 shrink-0 rounded-full',
                activeZone?.zone_state === 'ACTIVE' ? 'bg-[var(--accent)]' : 'bg-muted-foreground',
              )}
            />
            <span className="min-w-0">
              <span className="block text-[0.6875rem] tracking-[0.1em] text-muted-foreground uppercase">
                Zone
              </span>
              <span className="block truncate font-mono text-sm text-foreground">
                {activeZone?.name ?? 'Select a zone'}
              </span>
            </span>
          </span>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[16rem]">
        <DropdownMenuLabel>Zones you can reach</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {zones.map((zone) => (
          <DropdownMenuItem
            key={zone.id}
            onSelect={() => {
              selectZone(zone.id)
            }}
            className="gap-2"
          >
            <CheckIcon
              className={cn('size-3.5', zone.id === activeZone?.id ? 'opacity-100' : 'opacity-0')}
            />
            <span className="min-w-0 flex-1 truncate font-mono">{zone.name}</span>
            {zone.zone_state === 'ACTIVE' ? null : (
              <span className="text-[0.6875rem] text-muted-foreground">
                {zone.zone_state === 'ARCHIVED' ? 'archived' : 'paused'}
              </span>
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/zones">
            <PlusIcon className="size-3.5" />
            Manage zones
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
